import { CSSProperties, FC, MouseEvent, useState } from "react";
import toast from "react-hot-toast";
import { requestCarPrice } from "api/vehicles/vehicle";

// Shared by Designs A, B and C: each passes its own className/style so the
// button looks native. It sits inside clickable cards, so a press must not
// reach the card.
export interface RequestPriceButtonProps {
  car: { unique_identifier?: string };
  className?: string;
  style?: CSSProperties;
}

// The backend half of 1NOW-11077 (POST fleet/request-price/) is on dev and QA only. On stage and production the
// button could only fail, so it shows where the route exists (Taha, 8 Oct). Drop this gate when 11077's backend
// is promoted. Same build-time check as lib/devPreview: CRA inlines both values, so stage and prod fold it to false.
const PRICE_REQUESTS_LIVE =
  process.env.NODE_ENV !== "production" ||
  process.env.REACT_APP_ENV === "dev" ||
  process.env.REACT_APP_ENV === "qa" ||
  process.env.REACT_APP_ENV === "local";

const storageKey = (uuid: string) => `price_requested_${uuid}`;

// sessionStorage can throw (private mode, embedded webviews): never let that
// break the card, the button just forgets it was pressed.
const wasRequested = (uuid: string) => {
  try {
    return sessionStorage.getItem(storageKey(uuid)) === "1";
  } catch {
    return false;
  }
};

const remember = (uuid: string) => {
  try {
    sessionStorage.setItem(storageKey(uuid), "1");
  } catch {
    // nothing to do: the sent state still holds for this mount
  }
};

const RequestPriceButton: FC<RequestPriceButtonProps> = ({
  car,
  className = "",
  style,
}) => {
  const uuid = car.unique_identifier;
  const [loading, setLoading] = useState(false);
  const [sentUuid, setSentUuid] = useState<string | null>(null);
  if (!PRICE_REQUESTS_LIVE || !uuid) return null;

  const sent = sentUuid === uuid || wasRequested(uuid);

  const onClick = async (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading || sent) return;
    setLoading(true);
    const { error } = await requestCarPrice(uuid);
    setLoading(false);
    if (error) {
      toast.error(error);
      return;
    }
    remember(uuid);
    setSentUuid(uuid);
    toast.success("Thanks! We'll add a price for this car soon.");
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading || sent}
      className={`${className} ${
        loading || sent ? "opacity-60 cursor-default" : ""
      }`}
      style={style}
    >
      {loading ? "Sending..." : sent ? "Price requested" : "Request price"}
    </button>
  );
};

export default RequestPriceButton;
